import { useState, useEffect, useRef } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useAdminStore } from '../../store/adminStore'

const EXTRA_LANGS = ['en', 'uz'] as const
type ExtraLang = typeof EXTRA_LANGS[number]
type ItemLang = 'ru' | ExtraLang

interface TaskLangTrans { title: string; description: string }
interface ItemLangTrans { product_name: string; product_description: string }

interface ItemRow {
  product_name: string
  product_description: string
  category_id: string
  imageFile?: File
  imagePreview?: string
  translations?: Partial<Record<ExtraLang, ItemLangTrans>>
  activeLang: ItemLang
}

const inputStyle = {
  background: 'var(--app-card-bg)',
  color: 'var(--tg-theme-text-color)',
  border: '1px solid transparent',
  borderRadius: '12px',
  padding: '10px 12px',
  width: '100%',
  fontSize: '16px',
  outline: 'none',
}

const selectStyle = {
  ...inputStyle,
  appearance: 'none' as const,
  cursor: 'pointer',
}

function Toggle({
  value,
  onChange,
  label,
}: {
  value: boolean
  onChange: (v: boolean) => void
  label: string
}) {
  return (
    <div
      className="flex items-center gap-3 cursor-pointer"
      onClick={() => onChange(!value)}
    >
      <div
        className="relative w-10 h-6 rounded-full flex-shrink-0 transition-colors"
        style={{ background: value ? 'var(--tg-theme-button-color)' : 'var(--app-card-bg)' }}
      >
        <div
          className="absolute top-1 w-4 h-4 rounded-full bg-white shadow transition-transform"
          style={{ transform: value ? 'translateX(20px)' : 'translateX(4px)' }}
        />
      </div>
      <span className="text-sm" style={{ color: 'var(--tg-theme-text-color)' }}>
        {label}
      </span>
    </div>
  )
}

export default function AdminCreateTaskPage() {
  const navigate = useNavigate()
  const { taskId } = useParams<{ taskId?: string }>()
  const isEditing = !!taskId
  const { createTask, updateTask, publishTask, fetchTaskDetail, fetchCategories, uploadTaskImage, uploadTaskItemImage, taskDetail, categories } = useAdminStore()
  const itemFileInputRef = useRef<HTMLInputElement>(null)
  const [pendingImageIndex, setPendingImageIndex] = useState<number | null>(null)

  const defaultExpires = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)
    .toISOString()
    .slice(0, 16)

  const [activeLang, setActiveLang] = useState<'ru' | ExtraLang>('ru')
  const [taskTrans, setTaskTrans] = useState<Partial<Record<ExtraLang, TaskLangTrans>>>({})

  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [taskType, setTaskType] = useState<'spot' | 'batch'>('spot')
  const [categoryId, setCategoryId] = useState('')
  const [locationName, setLocationName] = useState('')
  const [expiresAt, setExpiresAt] = useState(defaultExpires)
  const [maxClaims, setMaxClaims] = useState('1')
  const [rewardPerItem, setRewardPerItem] = useState('')
  const [headerImageUrl, setHeaderImageUrl] = useState('')
  const [imageUploading, setImageUploading] = useState(false)
  const [publishNow, setPublishNow] = useState(true)
  const [items, setItems] = useState<ItemRow[]>([{ product_name: '', product_description: '', category_id: '', activeLang: 'ru' }])
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isLoadingTask, setIsLoadingTask] = useState(isEditing)

  useEffect(() => {
    fetchCategories()
  }, [fetchCategories])

  useEffect(() => {
    if (isEditing && taskId) {
      fetchTaskDetail(taskId).then(() => setIsLoadingTask(false))
    }
  }, [isEditing, taskId, fetchTaskDetail])

  // Populate form when editing
  useEffect(() => {
    if (isEditing && taskDetail && taskDetail.id === taskId) {
      setTitle(taskDetail.title)
      setDescription(taskDetail.description || '')
      setTaskType(taskDetail.task_type)
      setCategoryId(taskDetail.category_id || '')
      setLocationName(taskDetail.target_location_name || '')
      setExpiresAt(new Date(taskDetail.expires_at).toISOString().slice(0, 16))
      setMaxClaims(String(taskDetail.max_claims))
      setRewardPerItem(taskDetail.reward_per_item_coins ? String(taskDetail.reward_per_item_coins) : '')
      setHeaderImageUrl(taskDetail.header_image_url || '')
      setPublishNow(false) // don't auto-publish on edit
      if (taskDetail.translations) {
        const trans: Partial<Record<ExtraLang, TaskLangTrans>> = {}
        for (const lang of EXTRA_LANGS) {
          const t = taskDetail.translations[lang]
          if (t) trans[lang] = { title: t.title ?? '', description: t.description ?? '' }
        }
        setTaskTrans(trans)
      }
      setItems(
        taskDetail.items.length > 0
          ? taskDetail.items.map((i) => {
              const itemTrans: Partial<Record<ExtraLang, ItemLangTrans>> = {}
              if (i.translations) {
                for (const lang of EXTRA_LANGS) {
                  const t = i.translations[lang]
                  if (t) itemTrans[lang] = { product_name: t.product_name ?? '', product_description: t.product_description ?? '' }
                }
              }
              return {
                product_name: i.product_name,
                product_description: i.product_description || '',
                category_id: i.category_id || '',
                translations: itemTrans,
                activeLang: 'ru' as ItemLang,
              }
            })
          : [{ product_name: '', product_description: '', category_id: '', activeLang: 'ru' as ItemLang }]
      )
    }
  }, [isEditing, taskDetail, taskId])

  const addItem = () =>
    setItems((prev) => [...prev, { product_name: '', product_description: '', category_id: '', activeLang: 'ru' }])

  const removeItem = (i: number) =>
    setItems((prev) => {
      const item = prev[i]
      if (item.imagePreview) URL.revokeObjectURL(item.imagePreview)
      return prev.filter((_, idx) => idx !== i)
    })

  const updateItem = (i: number, field: 'product_name' | 'product_description' | 'category_id', value: string) =>
    setItems((prev) =>
      prev.map((item, idx) => (idx === i ? { ...item, [field]: value } : item))
    )

  const updateItemTrans = (i: number, lang: ExtraLang, field: 'product_name' | 'product_description', value: string) =>
    setItems((prev) =>
      prev.map((item, idx) => {
        if (idx !== i) return item
        const prev_trans = item.translations ?? {}
        const prev_lang = prev_trans[lang] ?? { product_name: '', product_description: '' }
        return { ...item, translations: { ...prev_trans, [lang]: { ...prev_lang, [field]: value } } }
      })
    )

  const updateItemLang = (i: number, lang: ItemLang) =>
    setItems((prev) => prev.map((item, idx) => idx === i ? { ...item, activeLang: lang } : item))

  const setItemImage = (i: number, file: File) => {
    setItems((prev) =>
      prev.map((item, idx) => {
        if (idx !== i) return item
        if (item.imagePreview) URL.revokeObjectURL(item.imagePreview)
        return { ...item, imageFile: file, imagePreview: URL.createObjectURL(file) }
      })
    )
  }

  const clearItemImage = (i: number) => {
    setItems((prev) =>
      prev.map((item, idx) => {
        if (idx !== i) return item
        if (item.imagePreview) URL.revokeObjectURL(item.imagePreview)
        return { ...item, imageFile: undefined, imagePreview: undefined }
      })
    )
  }

  const handleSubmit = async () => {
    if (!title.trim() || !expiresAt) return
    const validItems = items.filter((i) => i.product_name.trim())
    if (!isEditing && validItems.length === 0) {
      alert('Add at least one item with a product name')
      return
    }

    // Build task translations (only non-empty)
    const builtTaskTrans: Record<string, { title?: string; description?: string }> = {}
    for (const lang of EXTRA_LANGS) {
      const lt = taskTrans[lang]
      if (lt?.title.trim() || lt?.description.trim()) {
        builtTaskTrans[lang] = { title: lt.title.trim() || undefined, description: lt.description.trim() || undefined }
      }
    }

    setIsSubmitting(true)
    try {
      if (isEditing && taskId) {
        await updateTask(taskId, {
          title: title.trim(),
          description: description.trim() || undefined,
          category_id: categoryId || undefined,
          target_location_name: locationName.trim() || undefined,
          expires_at: new Date(expiresAt).toISOString(),
          max_claims: parseInt(maxClaims) || 1,
          reward_per_item_coins: rewardPerItem ? parseInt(rewardPerItem) : undefined,
          header_image_url: headerImageUrl || undefined,
          translations: Object.keys(builtTaskTrans).length > 0 ? builtTaskTrans : undefined,
        })
        navigate(`/admin/tasks/${taskId}`)
      } else {
        const task = await createTask({
          title: title.trim(),
          description: description.trim() || undefined,
          task_type: taskType,
          category_id: categoryId || undefined,
          target_location_name: locationName.trim() || undefined,
          expires_at: new Date(expiresAt).toISOString(),
          max_claims: parseInt(maxClaims) || 1,
          reward_per_item_coins: rewardPerItem ? parseInt(rewardPerItem) : undefined,
          header_image_url: headerImageUrl || undefined,
          translations: Object.keys(builtTaskTrans).length > 0 ? builtTaskTrans : undefined,
          items: validItems.map((item, idx) => {
            const builtItemTrans: Record<string, { product_name?: string; product_description?: string }> = {}
            for (const lang of EXTRA_LANGS) {
              const lt = item.translations?.[lang]
              if (lt?.product_name.trim() || lt?.product_description.trim()) {
                builtItemTrans[lang] = { product_name: lt.product_name.trim() || undefined, product_description: lt.product_description.trim() || undefined }
              }
            }
            return {
              product_name: item.product_name.trim(),
              product_description: item.product_description.trim() || undefined,
              category_id: item.category_id || undefined,
              sort_order: idx,
              translations: Object.keys(builtItemTrans).length > 0 ? builtItemTrans : undefined,
            }
          }),
        })
        // Upload images for items that have a file selected, matched by sort_order index
        const sortedItems = [...task.items].sort((a, b) => a.sort_order - b.sort_order)
        await Promise.all(
          validItems.map((item, idx) => {
            if (!item.imageFile) return Promise.resolve()
            const taskItem = sortedItems[idx]
            if (!taskItem) return Promise.resolve()
            return uploadTaskItemImage(task.id, taskItem.id, item.imageFile)
          })
        )
        if (publishNow) await publishTask(task.id)
        navigate('/admin/tasks')
      }
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : `Failed to ${isEditing ? 'update' : 'create'} task`)
      setIsSubmitting(false)
    }
  }

  if (isLoadingTask) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="text-sm opacity-50" style={{ color: 'var(--tg-theme-text-color)' }}>
          Loading...
        </div>
      </div>
    )
  }

  return (
    <div className="p-4 space-y-4 pb-28">
      <button
        className="text-sm opacity-60"
        style={{ color: 'var(--tg-theme-link-color)' }}
        onClick={() => navigate(-1)}
      >
        ← Back
      </button>

      <div className="flex items-center justify-between">
        <h2 className="text-base font-bold" style={{ color: 'var(--tg-theme-text-color)' }}>
          {isEditing ? 'Edit Task' : 'New Task'}
        </h2>
        {/* Language tab bar */}
        <div className="flex rounded-xl overflow-hidden text-xs font-semibold" style={{ background: 'var(--app-card-bg)' }}>
          {(['ru', ...EXTRA_LANGS] as const).map((lang) => {
            const hasContent = lang !== 'ru' && (taskTrans[lang]?.title?.trim() || taskTrans[lang]?.description?.trim())
            return (
              <button
                key={lang}
                className="px-3 py-1.5 uppercase relative"
                style={{
                  background: activeLang === lang ? 'var(--tg-theme-button-color)' : 'transparent',
                  color: activeLang === lang ? 'var(--tg-theme-button-text-color)' : 'var(--tg-theme-text-color)',
                }}
                onClick={() => setActiveLang(lang)}
              >
                {lang}
                {hasContent && activeLang !== lang && (
                  <span className="absolute top-1 right-1 w-1.5 h-1.5 rounded-full bg-green-500" />
                )}
              </button>
            )
          })}
        </div>
      </div>

      {/* Title */}
      <input
        style={inputStyle}
        placeholder={activeLang === 'ru' ? 'Title *' : `Title (${activeLang.toUpperCase()}, optional)`}
        value={activeLang === 'ru' ? title : (taskTrans[activeLang]?.title ?? '')}
        onChange={(e) => {
          if (activeLang === 'ru') {
            setTitle(e.target.value)
          } else {
            const lang = activeLang
            setTaskTrans((prev) => ({ ...prev, [lang]: { ...{ title: '', description: '' }, ...prev[lang], title: e.target.value } }))
          }
        }}
      />

      {/* Description */}
      <textarea
        style={{ ...inputStyle, minHeight: '64px', resize: 'none' }}
        placeholder={activeLang === 'ru' ? 'Description (optional)' : `Description (${activeLang.toUpperCase()}, optional)`}
        value={activeLang === 'ru' ? description : (taskTrans[activeLang]?.description ?? '')}
        onChange={(e) => {
          if (activeLang === 'ru') {
            setDescription(e.target.value)
          } else {
            const lang = activeLang
            setTaskTrans((prev) => ({ ...prev, [lang]: { ...{ title: '', description: '' }, ...prev[lang], description: e.target.value } }))
          }
        }}
      />

      {/* Header image */}
      <div>
        <p className="text-xs font-medium opacity-50 mb-1.5" style={{ color: 'var(--tg-theme-text-color)' }}>
          Header image (optional)
        </p>
        {headerImageUrl ? (
          <div className="relative rounded-xl overflow-hidden" style={{ aspectRatio: '16/7' }}>
            <img src={headerImageUrl} alt="Header" className="w-full h-full object-cover" />
            {imageUploading && (
              <div className="absolute inset-0 flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.45)' }}>
                <span className="text-sm text-white font-medium">Uploading…</span>
              </div>
            )}
            {!imageUploading && (
              <button
                className="absolute top-2 right-2 text-xs px-2 py-1 rounded-lg font-medium"
                style={{ background: '#ef4444', color: 'white' }}
                onClick={() => setHeaderImageUrl('')}
              >
                Remove
              </button>
            )}
          </div>
        ) : (
          <label
            className="flex flex-col items-center justify-center rounded-xl cursor-pointer"
            style={{
              border: '2px dashed var(--tg-theme-hint-color)',
              minHeight: '80px',
              color: 'var(--tg-theme-hint-color)',
            }}
          >
            <>
              <span className="text-lg">🖼</span>
              <span className="text-xs mt-1">Tap to upload image</span>
            </>
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp"
              className="hidden"
              onChange={async (e) => {
                const file = e.target.files?.[0]
                if (!file) return
                // Show local preview immediately
                const localUrl = URL.createObjectURL(file)
                setHeaderImageUrl(localUrl)
                setImageUploading(true)
                try {
                  const url = await uploadTaskImage(file)
                  setHeaderImageUrl(url)
                  URL.revokeObjectURL(localUrl)
                } catch (err: unknown) {
                  setHeaderImageUrl('')
                  URL.revokeObjectURL(localUrl)
                  alert(err instanceof Error ? err.message : 'Upload failed')
                } finally {
                  setImageUploading(false)
                }
              }}
            />
          </label>
        )}
      </div>

      {/* Task type (create only) */}
      {!isEditing && (
        <div>
          <p className="text-xs font-medium opacity-50 mb-1.5" style={{ color: 'var(--tg-theme-text-color)' }}>
            Task type
          </p>
          <div className="flex rounded-xl overflow-hidden" style={{ background: 'var(--app-card-bg)' }}>
            {(['spot', 'batch'] as const).map((t) => (
              <button
                key={t}
                className="flex-1 py-2 text-sm font-medium capitalize"
                style={{
                  background: taskType === t ? 'var(--tg-theme-button-color)' : 'transparent',
                  color:
                    taskType === t
                      ? 'var(--tg-theme-button-text-color)'
                      : 'var(--tg-theme-text-color)',
                }}
                onClick={() => setTaskType(t)}
              >
                {t}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Category */}
      {categories.length > 0 && (
        <div>
          <p className="text-xs font-medium opacity-50 mb-1.5" style={{ color: 'var(--tg-theme-text-color)' }}>
            Category (optional)
          </p>
          <select
            style={selectStyle}
            value={categoryId}
            onChange={(e) => setCategoryId(e.target.value)}
          >
            <option value="">— No category —</option>
            {categories.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Location */}
      <input
        style={inputStyle}
        placeholder="Location name (e.g. Chorsu Market)"
        value={locationName}
        onChange={(e) => setLocationName(e.target.value)}
      />

      {/* Expiry + max claims */}
      <div className="grid grid-cols-2 gap-2">
        <div>
          <p className="text-xs font-medium opacity-50 mb-1.5" style={{ color: 'var(--tg-theme-text-color)' }}>
            Expires at *
          </p>
          <input
            type="datetime-local"
            style={inputStyle}
            value={expiresAt}
            onChange={(e) => setExpiresAt(e.target.value)}
          />
        </div>
        <div>
          <p className="text-xs font-medium opacity-50 mb-1.5" style={{ color: 'var(--tg-theme-text-color)' }}>
            Max claims
          </p>
          <input
            type="number"
            min="1"
            style={inputStyle}
            value={maxClaims}
            onChange={(e) => setMaxClaims(e.target.value)}
          />
        </div>
      </div>

      {/* Reward per item */}
      <div>
        <p className="text-xs font-medium opacity-50 mb-1.5" style={{ color: 'var(--tg-theme-text-color)' }}>
          Reward per item (Coins)
        </p>
        <input
          type="number"
          min="0"
          style={inputStyle}
          placeholder="e.g. 50 (optional)"
          value={rewardPerItem}
          onChange={(e) => setRewardPerItem(e.target.value)}
        />
      </div>

      {/* Items (create only) */}
      {!isEditing && (
        <div>
          <p className="text-xs font-medium opacity-50 mb-2 uppercase" style={{ color: 'var(--tg-theme-text-color)' }}>
            Products to find
          </p>
          <div className="space-y-2">
            {items.map((item, i) => (
              <div
                key={i}
                className="rounded-xl p-3 space-y-2"
                style={{ background: 'var(--app-card-bg)' }}
              >
                <div className="flex items-center justify-between gap-2">
                  <span
                    className="text-xs opacity-40 font-medium flex-shrink-0"
                    style={{ color: 'var(--tg-theme-text-color)' }}
                  >
                    #{i + 1}
                  </span>
                  {/* Per-item language tabs + remove */}
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <div className="flex items-center gap-1">
                      {(['ru', 'en', 'uz'] as ItemLang[]).map((lang) => {
                        const hasContent = lang !== 'ru' && (
                          item.translations?.[lang as ExtraLang]?.product_name?.trim() ||
                          item.translations?.[lang as ExtraLang]?.product_description?.trim()
                        )
                        return (
                          <button
                            key={lang}
                            className="px-2 py-0.5 rounded-md text-xs font-semibold uppercase relative"
                            style={{
                              background: item.activeLang === lang ? 'var(--tg-theme-button-color)' : 'var(--app-page-bg)',
                              color: item.activeLang === lang ? 'var(--tg-theme-button-text-color)' : 'var(--tg-theme-hint-color)',
                            }}
                            onClick={() => updateItemLang(i, lang)}
                          >
                            {lang}
                            {hasContent && item.activeLang !== lang && (
                              <span className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 rounded-full bg-green-500" />
                            )}
                          </button>
                        )
                      })}
                    </div>
                    {items.length > 1 && (
                      <button
                        className="text-xs px-2 py-0.5 rounded-lg"
                        style={{ background: '#ef444418', color: '#ef4444' }}
                        onClick={() => removeItem(i)}
                      >
                        Remove
                      </button>
                    )}
                  </div>
                </div>
                <input
                  style={{ ...inputStyle, background: 'var(--app-page-bg)' }}
                  placeholder={item.activeLang === 'ru' ? 'Product name *' : `Product name (${item.activeLang.toUpperCase()}, optional)`}
                  value={item.activeLang === 'ru' ? item.product_name : (item.translations?.[item.activeLang as ExtraLang]?.product_name ?? '')}
                  onChange={(e) => {
                    if (item.activeLang === 'ru') updateItem(i, 'product_name', e.target.value)
                    else updateItemTrans(i, item.activeLang as ExtraLang, 'product_name', e.target.value)
                  }}
                />
                <input
                  style={{ ...inputStyle, background: 'var(--app-page-bg)' }}
                  placeholder={item.activeLang === 'ru' ? 'Description (optional)' : `Description (${item.activeLang.toUpperCase()}, optional)`}
                  value={item.activeLang === 'ru' ? item.product_description : (item.translations?.[item.activeLang as ExtraLang]?.product_description ?? '')}
                  onChange={(e) => {
                    if (item.activeLang === 'ru') updateItem(i, 'product_description', e.target.value)
                    else updateItemTrans(i, item.activeLang as ExtraLang, 'product_description', e.target.value)
                  }}
                />
                {categories.length > 0 && (
                  <select
                    style={{ ...selectStyle, background: 'var(--app-page-bg)' }}
                    value={item.category_id}
                    onChange={(e) => updateItem(i, 'category_id', e.target.value)}
                  >
                    <option value="">— Category (optional) —</option>
                    {categories.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                )}
                {/* Reference image */}
                <div>
                  <p className="text-xs opacity-40 mb-1.5" style={{ color: 'var(--tg-theme-text-color)' }}>
                    Reference image (optional)
                  </p>
                  {item.imagePreview ? (
                    <div className="relative rounded-xl overflow-hidden" style={{ width: 80, height: 80 }}>
                      <img src={item.imagePreview} alt="" className="w-full h-full object-cover" />
                      <button
                        className="absolute top-1 right-1 w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold"
                        style={{ background: '#ef4444', color: 'white', lineHeight: 1 }}
                        onClick={() => clearItemImage(i)}
                      >
                        ✕
                      </button>
                    </div>
                  ) : (
                    <button
                      className="rounded-xl flex flex-col items-center justify-center"
                      style={{
                        width: 80, height: 80,
                        border: '2px dashed var(--tg-theme-hint-color)',
                        color: 'var(--tg-theme-hint-color)',
                        fontSize: 22,
                      }}
                      onClick={() => {
                        setPendingImageIndex(i)
                        itemFileInputRef.current?.click()
                      }}
                    >
                      +
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
          {/* Hidden file input shared across all item image pickers */}
          <input
            ref={itemFileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0]
              e.target.value = ''
              if (!file || pendingImageIndex === null) return
              setItemImage(pendingImageIndex, file)
              setPendingImageIndex(null)
            }}
          />
          <button
            className="w-full py-2 mt-2 rounded-xl text-sm"
            style={{ background: 'var(--app-card-bg)', color: 'var(--tg-theme-link-color)' }}
            onClick={addItem}
          >
            + Add Item
          </button>
        </div>
      )}

      {/* Publish toggle (create only) */}
      {!isEditing && (
        <Toggle value={publishNow} onChange={setPublishNow} label="Publish immediately" />
      )}

      {/* Submit */}
      <div
        className="fixed bottom-0 left-0 right-0 p-4 safe-area-bottom"
        style={{ background: 'var(--app-page-bg)' }}
      >
        <button
          className="w-full py-3 rounded-2xl font-semibold text-base disabled:opacity-40"
          style={{
            background: 'var(--tg-theme-button-color)',
            color: 'var(--tg-theme-button-text-color)',
          }}
          disabled={isSubmitting || !title.trim()}
          onClick={handleSubmit}
        >
          {isSubmitting
            ? isEditing
              ? 'Saving...'
              : 'Creating...'
            : isEditing
            ? 'Save Changes'
            : publishNow
            ? 'Create & Publish'
            : 'Save as Draft'}
        </button>
      </div>
    </div>
  )
}
